import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useAuditDashboardAuditDetails } from "../../hooks";
import { AuditDashboardScratcherTab, countGames, countMismatchedGames, countPacks } from "../../utils";
import AuditDetailScratcherTable from "./AuditDetailScratcherTable";
import ShiftActivity from "./ShiftActivity";
import ShiftReceiptSection from "./receipts/ShiftReceiptSection";
import ShiftVideoSection from "./receipts/ShiftVideoSection";

interface ShiftScratcherDetailsProps {
  storeId?: string;
  auditId?: string | null;
  reportDate?: string | null;
  isExpanded: boolean;
  hasPosType?: boolean;
  ignorePosData?: boolean;
  defaultTab?: AuditDashboardScratcherTab;
}

export default function ShiftScratcherDetails({
  storeId,
  auditId,
  reportDate,
  isExpanded,
  hasPosType = true,
  ignorePosData = false,
  defaultTab = "all",
}: ShiftScratcherDetailsProps) {
  const [activeTab, setActiveTab] = useState<AuditDashboardScratcherTab>(defaultTab);
  const auditDetailsQuery = useAuditDashboardAuditDetails(storeId, auditId, isExpanded);
  const auditDetails = auditDetailsQuery.data;
  const canUsePosData = hasPosType && !ignorePosData;

  const auditItems = auditDetails?.items;
  const movedBackstockItems = auditDetails?.movedToBackstockItems;
  const allRows = useMemo(() => auditItems ?? [], [auditItems]);
  const movedRows = useMemo(() => movedBackstockItems ?? [], [movedBackstockItems]);
  const activeDefaultRows = useMemo(
    () => (activeTab === "mismatched" ? allRows.filter((item) => item.hasVariance) : allRows),
    [activeTab, allRows]
  );
  const scratcherGamesCount = countGames(allRows);
  const mismatchedCount = countMismatchedGames(allRows);
  const movedCount = movedRows.length;
  const totalPacksCount = activeTab === "moved_to_backstock" ? movedRows.length : countPacks(activeDefaultRows);

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: AuditDashboardScratcherTab) => {
    setActiveTab(nextTab);
  };

  useEffect(() => {
    if (isExpanded) {
      setActiveTab(canUsePosData ? defaultTab : "all");
    }
  }, [auditId, canUsePosData, defaultTab, isExpanded]);

  useEffect(() => {
    if (!canUsePosData && activeTab === "mismatched") {
      setActiveTab("all");
    }
  }, [activeTab, canUsePosData]);

  if (!auditId) {
    return (
      <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
        No shift audit detail found.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "transparent",
        backgroundImage: "none",
      }}
    >
      {auditDetailsQuery.isError ? (
        <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography component="div" sx={{ fontSize: 12, color: "text.secondary" }}>
            Unable to load shift audit detail.
          </Typography>
        </Box>
      ) : (
        <AuditDetailScratcherTable
          activeTab={activeTab}
          scratcherCount={scratcherGamesCount}
          mismatchedCount={mismatchedCount}
          movedCount={movedCount}
          defaultRows={activeDefaultRows}
          movedRows={movedRows}
          hasPosType={canUsePosData}
          isLoading={auditDetailsQuery.isLoading || (auditDetailsQuery.isFetching && !auditDetails)}
          totalPacksCount={totalPacksCount}
          auditNotes={auditDetails?.noteEntries ?? []}
          onTabChange={handleTabChange}
          storeId={storeId}
        />
      )}

      <ShiftActivity activities={auditDetails?.shiftActivity ?? []} />
      <ShiftVideoSection auditId={auditId} enabled={isExpanded} hasVideo={Boolean(auditDetails?.hasVideo)} />
      <ShiftReceiptSection storeId={storeId} auditId={auditId} reportDate={reportDate} enabled={isExpanded} />
    </Box>
  );
}
